<template>
    <v-layout align-center justify-center class="auth-layout">
        <v-progress-linear indeterminate fixed v-if="loading" style="top:0"/>
        <v-sheet
            v-if="preAuthDynamicKey.enabled && preAuthDynamicKey.code"
            rounded
            role="button"
            title="Copiar clave dinámica"
            class="auth-dynamic-key-compact auth-dynamic-key-top px-3 py-2"
            @click="copyPreAuthDynamicKey">
            <div class="d-flex align-center justify-center">
                <v-icon small color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                <span class="caption primary--text font-weight-bold mr-2">Clave dinámica</span>
                <span class="subtitle-2 font-weight-bold primary--text">{{ preAuthDynamicKey.code }}</span>
                <v-chip x-small label color="secondary" class="primary--text font-weight-bold ml-2">
                    {{ preAuthDynamicKey.remaining }}s
                </v-chip>
            </div>
            <v-progress-linear
                :value="preAuthDynamicKeyProgress"
                color="primary"
                background-color="rgba(13, 147, 165, .16)"
                height="4"
                rounded
                class="mt-2"/>
        </v-sheet>
        <v-flex xs12 sm9 md6 lg4 class="py-4">
            <v-img :src="'/img/logo-valzum.png'" class="px-6" max-height="120" contain/>
            <v-card flat color="transparent" class="ma-3">
                <v-toolbar flat :color="'grey ' + (!$vuetify.theme.dark ? 'light' : 'dark') + 'en-4'" class="mb-4">
                    <v-toolbar-title
                        class="text-center font-weight-bold primary--text text-subtitle-1 text-sm-h5"
                        style="white-space: normal; width: 100%">INGRESA A TU CUENTA
                    </v-toolbar-title>
                    <div class="flex-grow-1"></div>
                </v-toolbar>
                <v-form ref="form" @submit.prevent="submit" lazy-validation v-model="valid">
                    <v-card-text class="px-0 pb-0">
                        <v-alert
                            v-if="notice.message"
                            :type="notice.type"
                            dense
                            text
                            class="mb-4"
                        >{{ notice.message }}</v-alert>
                        <v-text-field
                            :label="labels.email"
                            v-model="form.email"
                            type="email"
                            name="email"
                            :error-messages="errors.email"
                            :rules="[rules.required('email'), rules.mail('email')]"
                            :disabled="loading"
                            prepend-inner-icon="mdi-shield-account"
                            @input="clearErrors('email')"
                            filled/>
                        <v-text-field
                            :label="labels.password"
                            v-model="form.password"
                            :append-icon="passwordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                            @click:append="() => (passwordHidden = !passwordHidden)"
                            :type="passwordHidden ? 'password' : 'text'"
                            name="password"
                            :error-messages="errors.password"
                            :disabled="loading"
                            :rules="[rules.req('password')]"
                            prepend-inner-icon="mdi-shield-lock"
                            @input="clearErrors('password')"
                            filled/>
                        <input
                            v-model="form.website_url"
                            class="auth-hp-field"
                            tabindex="-1"
                            autocomplete="off"
                            name="website_url"
                            type="text"
                            aria-hidden="true">
                    </v-card-text>
                    <v-card-actions class="px-0">
                        <v-btn
                            type="submit"
                            :loading="loading"
                            :disabled="loading || !valid"
                            color="primary"
                            class="font-weight-bold"
                            block x-large
                        >Ingresar</v-btn>
                    </v-card-actions>
                    <v-btn
                        text
                        block
                        color="primary"
                        large
                        :loading="passkeyLoading"
                        :disabled="loading || passkeyLoading || !passkeySupported"
                        @click="loginWithPasskey">
                        <v-icon left>mdi-fingerprint</v-icon>Ingresar con llave de acceso
                    </v-btn>
                    <v-btn
                        v-if="verification.email"
                        text block color="primary"
                        :loading="verification.loading"
                        :disabled="loading || verification.loading"
                        large
                        @click="resendVerification"
                    ><v-icon left>mdi-email-sync</v-icon>Reenviar validación de correo</v-btn>
                    <v-btn
                        text block color="tertiary" :disabled="loading" large
                        :to="form.email ? {name: 'Forgot', query: {email: form.email}} : {name: 'Forgot'}"
                    ><v-icon left>mdi-lock-reset</v-icon>Recuperar mi contraseña</v-btn>
                </v-form>
            </v-card>
            <v-dialog v-model="twoFactor.required" persistent scrollable max-width="460px">
                <v-card class="auth-fixed-dialog">
                    <v-card-title class="primary secondary--text" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-shield-key</v-icon>
                        Confirmar acceso
                    </v-card-title>
                    <v-card-text class="auth-fixed-dialog-body pt-4">
                        <v-alert dense text type="info" class="mb-4">
                            {{ twoFactor.message || 'Confirma tu acceso con tu codigo de seguridad.' }}
                        </v-alert>
                        <v-select
                            v-if="twoFactor.methods.length > 1"
                            v-model="twoFactor.method"
                            :items="twoFactor.methods"
                            item-text="text"
                            item-value="value"
                            label="Metodo de seguridad"
                            prepend-inner-icon="mdi-shield-account"
                            filled/>
                        <v-chip v-else label color="secondary" class="primary--text font-weight-bold mb-4">
                            <v-icon left small>{{ selectedTwoFactorMethodIcon }}</v-icon>
                            {{ selectedTwoFactorMethodText }}
                        </v-chip>
                        <security-code-pad
                            v-if="twoFactor.method !== 'passkey'"
                            v-model="twoFactor.code"
                            :length="6"
                            :masked="false"
                            :error-messages="errors.code"
                            :disabled="twoFactor.loading"
                            autofocus
                            @input="clearErrors('code')"
                            @complete="verifyTwoFactor"
                            @submit="verifyTwoFactor"/>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="auth-fixed-dialog-footer">
                        <v-btn text color="error" :disabled="twoFactor.loading" @click="cancelTwoFactor">
                            Cancelar
                        </v-btn>
                        <v-spacer/>
                        <v-btn
                            v-if="twoFactor.method === 'email'"
                            text
                            color="primary"
                            :loading="twoFactor.resending"
                            :disabled="twoFactor.loading || twoFactor.resending"
                            @click="resendTwoFactorEmail">
                            Reenviar
                        </v-btn>
                        <v-btn
                            color="primary"
                            class="font-weight-bold"
                            :loading="twoFactor.loading"
                            :disabled="twoFactor.loading || twoFactorConfirmDisabled"
                            @click="verifyTwoFactor">
                            Confirmar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-card flat color="transparent" class="pb-4 pb-sm-6 pt-1 pt-md-0">
                <oauth-buttons/>
            </v-card>
            <div class="text-center py-2 py-sm-4 hidden-xs-only">
                ¿No tienes una cuenta?&nbsp;&nbsp;
                <v-btn color="secondary primary--text" to="/register">Crear Cuenta</v-btn>
            </div>
        </v-flex>
    </v-layout>
</template>

<script>
import axios from 'axios';
import Form from "../../helpers/form";
import OAuthButtons from "./providers/OAuthButtons";
import {devicePayload} from "../../helpers/device";
import {authenticatePasskey, isWebAuthnSupported} from "../../helpers/webauthn";
import SecurityCodePad from "../../components/SecurityCodePad";

export default {
    name: "Login",
    components: { 'oauth-buttons': OAuthButtons, SecurityCodePad },
    mixins: [ Form ],
    data: () => ({
        passwordHidden: true,
        labels: {
            email: 'Correo Electrónico',
            password: 'Contraseña',
        },
        form: {
            email: null,
            password: null,
            _auth_challenge: null,
            website_url: null,
            device_info: null,
        },
        notice: {
            type: 'info',
            message: ''
        },
        verification: {
            email: null,
            loading: false
        },
        preAuthDynamicKeyLoading: false,
        preAuthDynamicKey: {
            enabled: false,
            code: null,
            period: 30,
            remaining: 0,
            expires_at: null,
            timer: null
        },
        passkeySupported: false,
        passkeyLoading: false,
        twoFactor: {
            required: false,
            login_challenge_id: null,
            methods: [],
            method: 'email',
            code: null,
            message: '',
            loading: false,
            resending: false,
            timer: null
        },
        path: null
    }),
    computed: {
        selectedTwoFactorMethod() {
            return (this.twoFactor.methods || []).find(item => item.value === this.twoFactor.method) || {};
        },
        selectedTwoFactorMethodText() {
            return this.selectedTwoFactorMethod.text || 'Codigo de seguridad';
        },
        selectedTwoFactorMethodIcon() {
            return this.selectedTwoFactorMethod.icon || 'mdi-shield-key';
        },
        preAuthDynamicKeyProgress() {
            const period = Number(this.preAuthDynamicKey.period || 30);
            const remaining = Number(this.preAuthDynamicKey.remaining || 0);

            return period > 0 ? Math.max(0, Math.min(100, (remaining / period) * 100)) : 0;
        },
        twoFactorConfirmDisabled() {
            if (this.twoFactor.method === 'passkey') {
                return !this.passkeySupported;
            }
            return String(this.twoFactor.code || '').replace(/\D/g, '').length !== 6;
        },
        nativeDynamicKeyDevice() {
            const device = this.devicePayload();
            const deviceName = String(device.device || '').toLowerCase();

            return device.name === 'Valzum'
                && ['android', 'ios'].indexOf(deviceName) !== -1
                && !!device.device_uid;
        }
    },
    created() {
        if (!this.errors.code) {
            this.$set(this.errors, 'code', []);
        }

        this.form.email = this.$route.query.email || null;
        this.path = this.$route.query.to || null;
        this.passkeySupported = isWebAuthnSupported();
        if (this.$route.query.registered) {
            this.notice = {
                type: 'info',
                message: 'Cuenta creada. Revisa tu correo para validar el acceso antes de ingresar.'
            };
            this.verification.email = this.form.email;
        } else if (this.$route.query.verified) {
            this.notice = {
                type: 'success',
                message: 'Correo validado. Ya puedes ingresar.'
            };
        } else if (this.$route.query.oauth_error) {
            this.notice = {
                type: 'error',
                message: this.$route.query.oauth_error
            };
        }
        this.fetchChallenge('login');
        this.loadPreAuthDynamicKey();
    },
    beforeDestroy() {
        this.stopDynamicKeyTimer();
    },
    methods: {
        async submit() {
            if (this.$refs.form.validate()) {
                this.loading = true;
                this.form.device_info = this.devicePayload();

                if (!this.form._auth_challenge) {
                    await this.fetchChallenge('login');
                }

                axios.post('/api/login', this.form)
                    .then(res => {
                        if (res.data && res.data.code === 'two_factor_required') {
                            this.openTwoFactor(res.data);
                            return;
                        }

                        this.$toast.success('¡Bienvenido a Valzum! Te has identificado correctamente.!', {
                            icon: 'mdi-check-circle'
                        });
                        this.success(res.data);
                    })
                    .catch(err => {
                        const data = err.response && err.response.data ? err.response.data : {};
                        if (data.code === 'email_not_verified') {
                            this.verification.email = data.email || this.form.email;
                            this.notice = {
                                type: 'warning',
                                message: data.message || 'Antes de ingresar debes validar tu correo.'
                            };
                        }
                        this.handleErrors(data.errors);
                    })
                    .then(() => {
                        this.fetchChallenge('login');
                        this.loading = false;
                    })
            }
        },
        fetchChallenge(action) {
            return axios.get('/api/auth/challenge/' + action)
                .then(res => {
                    this.form._auth_challenge = res.data.token;
                })
                .catch(() => {
                    this.form._auth_challenge = null;
                });
        },
        resendVerification() {
            const email = this.verification.email || this.form.email;
            if (!email) return;

            this.verification.loading = true;
            axios.get('/api/auth/challenge/verification_resend')
                .then(res => axios.post('/api/email/verification/resend', {
                    email,
                    _auth_challenge: res.data.token,
                    website_url: this.form.website_url
                }))
                .then(() => {
                    this.$toast.success('Si el correo está pendiente, enviaremos un nuevo enlace.', {
                        icon: 'mdi-email-check'
                    });
                })
                .catch(err => {
                    const data = err.response && err.response.data ? err.response.data : {};
                    this.handleErrors(data.errors);
                })
                .then(() => {
                    this.verification.loading = false;
                });
        },
        async loginWithPasskey() {
            if (!this.passkeySupported || this.passkeyLoading) return;

            this.passkeyLoading = true;
            try {
                const options = await axios.post('/api/login/passkey/options', {
                    email: this.form.email || null
                });
                const credential = await authenticatePasskey(options.data.publicKey);
                const verified = await axios.post('/api/login/passkey/verify', {
                    challenge_id: options.data.challenge_id,
                    credential
                });

                this.$toast.success('Acceso confirmado.', {icon: 'mdi-check-circle'});
                this.success(verified.data);
            } catch (err) {
                const data = err.response && err.response.data ? err.response.data : {};
                if (data.code === 'email_not_verified') {
                    this.verification.email = data.email || this.form.email;
                    this.notice = {
                        type: 'warning',
                        message: data.message || 'Antes de ingresar debes validar tu correo.'
                    };
                }
                this.handleErrors(data.errors || {});
                this.$toast.error(this.errorMessage(err, 'No fue posible ingresar con llave de acceso.'), {icon: 'mdi-alert-circle'});
            } finally {
                this.passkeyLoading = false;
            }
        },
        openTwoFactor(data) {
            const methods = data.methods && data.methods.length ? data.methods : [
                { value: 'email', text: 'Codigo al correo', icon: 'mdi-email-lock' }
            ];

            this.twoFactor.required = true;
            this.twoFactor.login_challenge_id = data.login_challenge_id;
            this.twoFactor.methods = methods;
            this.twoFactor.method = methods[0].value;
            this.twoFactor.code = null;
            this.twoFactor.message = data.email_sent
                ? 'Enviamos un codigo a tu correo. Tambien puedes usar tu app autenticadora si esta activa.'
                : (data.message || 'Confirma tu acceso con tu codigo de seguridad.');
        },
        cancelTwoFactor() {
            this.twoFactor.required = false;
            this.twoFactor.login_challenge_id = null;
            this.twoFactor.code = null;
            this.form.password = null;
            this.fetchChallenge('login');
        },
        verifyTwoFactor() {
            if (this.twoFactor.method === 'passkey') {
                this.verifyTwoFactorPasskey();
                return;
            }

            this.twoFactor.code = String(this.twoFactor.code || '').replace(/\D/g, '').slice(0, 6);
            if (!this.twoFactor.login_challenge_id || this.twoFactor.code.length !== 6 || this.twoFactor.loading) return;

            this.twoFactor.loading = true;
            axios.post('/api/login/2fa', {
                login_challenge_id: this.twoFactor.login_challenge_id,
                method: this.twoFactor.method,
                code: this.twoFactor.code,
                device_info: this.devicePayload()
            }).then(res => {
                this.$toast.success('Acceso confirmado.', { icon: 'mdi-check-circle' });
                this.twoFactor.required = false;
                this.success(res.data);
            }).catch(err => {
                const data = err.response && err.response.data ? err.response.data : {};
                this.handleErrors(data.errors || {});
                this.$toast.error(this.errorMessage(err, 'No fue posible confirmar el acceso.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.twoFactor.loading = false;
            });
        },
        async verifyTwoFactorPasskey() {
            if (!this.twoFactor.login_challenge_id || !this.passkeySupported) return;

            this.twoFactor.loading = true;
            try {
                const options = await axios.post('/api/login/2fa/passkey/options', {
                    login_challenge_id: this.twoFactor.login_challenge_id
                });
                const credential = await authenticatePasskey(options.data.publicKey);
                const verified = await axios.post('/api/login/2fa/passkey/verify', {
                    login_challenge_id: this.twoFactor.login_challenge_id,
                    passkey_challenge_id: options.data.challenge_id,
                    credential
                });

                this.$toast.success('Acceso confirmado.', { icon: 'mdi-check-circle' });
                this.twoFactor.required = false;
                this.success(verified.data);
            } catch (err) {
                const data = err.response && err.response.data ? err.response.data : {};
                this.handleErrors(data.errors || {});
                this.$toast.error(this.errorMessage(err, 'No fue posible confirmar la llave de acceso.'), { icon: 'mdi-alert-circle' });
            } finally {
                this.twoFactor.loading = false;
            }
        },
        loadPreAuthDynamicKey() {
            if (!this.nativeDynamicKeyDevice || this.preAuthDynamicKeyLoading) return;

            this.preAuthDynamicKeyLoading = true;
            axios.post('/api/security/dynamic-key/device', {
                device_info: this.devicePayload()
            }).then(res => {
                this.setPreAuthDynamicKey(res.data.dynamic_key || null);
            }).catch(() => {
                this.setPreAuthDynamicKey(null);
            }).then(() => {
                this.preAuthDynamicKeyLoading = false;
            });
        },
        setPreAuthDynamicKey(payload) {
            this.stopDynamicKeyTimer();
            this.preAuthDynamicKey = Object.assign({
                enabled: false,
                code: null,
                period: 30,
                remaining: 0,
                expires_at: null,
                timer: null
            }, payload || {});

            if (this.preAuthDynamicKey.enabled && this.preAuthDynamicKey.expires_at) {
                this.startDynamicKeyTimer();
            } else {
                this.stopDynamicKeyTimer();
            }
        },
        startDynamicKeyTimer() {
            this.stopDynamicKeyTimer();

            if (!this.preAuthDynamicKey.enabled) return;

            this.tickDynamicKey();
            this.preAuthDynamicKey.timer = setInterval(() => {
                this.tickDynamicKey();
            }, 1000);
        },
        stopDynamicKeyTimer() {
            if (this.preAuthDynamicKey.timer) {
                clearInterval(this.preAuthDynamicKey.timer);
                this.preAuthDynamicKey.timer = null;
            }
        },
        tickDynamicKey() {
            if (!this.preAuthDynamicKey.enabled || !this.preAuthDynamicKey.expires_at) return;

            const expiresAt = Date.parse(this.preAuthDynamicKey.expires_at);
            if (!expiresAt) return;

            const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            this.$set(this.preAuthDynamicKey, 'remaining', remaining);

            if (remaining <= 1) {
                this.loadPreAuthDynamicKey();
            }
        },
        devicePayload() {
            return devicePayload();
        },
        copyPreAuthDynamicKey() {
            if (!this.preAuthDynamicKey.code) return;

            this.copyToClipboard(String(this.preAuthDynamicKey.code));
        },
        copyToClipboard(value) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(value).then(() => {
                    this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
                }).catch(() => {
                    this.copyToClipboardFallback(value);
                });
                return;
            }

            this.copyToClipboardFallback(value);
        },
        copyToClipboardFallback(value) {
            const input = document.createElement('textarea');
            input.value = value;
            input.setAttribute('readonly', 'readonly');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();

            try {
                document.execCommand('copy');
                this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
            } catch (error) {
                this.$toast.error('No se pudo copiar.', {icon: 'mdi-alert-circle'});
            } finally {
                document.body.removeChild(input);
            }
        },
        resendTwoFactorEmail() {
            if (!this.twoFactor.login_challenge_id) return;

            this.twoFactor.resending = true;
            axios.post('/api/login/2fa/email', {
                login_challenge_id: this.twoFactor.login_challenge_id
            }).then(res => {
                this.$toast.success(res.data.status || 'Codigo enviado.', { icon: 'mdi-email-check' });
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible enviar el codigo.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.twoFactor.resending = false;
            });
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            if (err.response && err.response.data && err.response.data.message) {
                return err.response.data.message;
            }

            return fallback;
        },
        success(data) {
            this.$store.dispatch('saveToken', data);
            this.$store.dispatch('setUser', data);
            if (this.path) {
                this.$router.push({path: this.path});
            } else {
                this.$router.push({name: 'Home'});
            }
        }
    }
}
</script>

<style scoped>
.auth-layout {
    min-height: 100%;
    position: relative;
}

.auth-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.auth-fixed-dialog-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.auth-fixed-dialog-footer {
    flex: 0 0 auto;
}

.auth-dynamic-key-compact {
    max-width: 280px;
    background: rgba(255, 255, 255, .72);
    border: 1px solid rgba(13, 147, 165, .18);
    cursor: pointer;
    font-variant-numeric: tabular-nums;
}

.auth-dynamic-key-top {
    left: 50%;
    position: fixed;
    top: calc(8px + env(safe-area-inset-top));
    transform: translateX(-50%);
    width: calc(100% - 24px);
    z-index: 5;
}

.theme--dark .auth-dynamic-key-compact {
    background: rgba(18, 52, 59, .72);
}
</style>
